import Spinner from '@rio-cloud/rio-uikit/Spinner';
import DOMPurify from 'dompurify';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { IPagesForUserManualDialog } from '@/features/userManual/utils';

const UserManualFunctionDialogContent = React.memo(({ keyPage, imageSrc, listKeys }: IPagesForUserManualDialog) => {
    const t = useIntl();
    const [showSpinner, setShowSpinner] = useState(true);
    const [imageLoaded, setImageLoaded] = useState(false);

    const sanitizeHtml = useCallback((key: string) => {
        const translatedHTML = t.formatMessage({ id: key });
        return DOMPurify.sanitize(translatedHTML);
    }, []);

    useEffect(() => {
        setShowSpinner(true);
        setImageLoaded(false);
    }, [imageSrc]);

    const handleImageLoad = () => {
        setImageLoaded(true);
        setShowSpinner(false);
    };

    const noteExists = t.formatMessage({ id: `userManual.${keyPage}.note` }) !== `userManual.${keyPage}.note`;

    return (
        <>
            <h4
                data-test-id='user-manual-modal-title'
                className='text-normal text-transform-none margin-top-20 margin-bottom-15'
            >
                <FormattedMessage id={`userManual.${keyPage}.title`} />
            </h4>
            <div className='position-relative width-100pct'>
                {showSpinner && (
                    <div className='display-flex align-items-center justify-content-center min-height-400'>
                        <Spinner />
                    </div>
                )}
                <img
                    src={imageSrc}
                    alt={t.formatMessage({ id: `userManual.${keyPage}.imageAlt` })}
                    data-test-id='user-manual-modal-image'
                    height={410}
                    className={`width-100pct height-100pct ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                    style={{ objectFit: 'contain' }}
                    onLoad={handleImageLoad}
                />
            </div>

            <p data-test-id='user-manual-modal-subtitle' className='text-bold margin-top-25 text-size-12'>
                <FormattedMessage id={`userManual.${keyPage}.subtitle`} />
            </p>
            <div className='counter color-secondary filled size-small'>
                {listKeys.map((key, index) => (
                    <div data-count={index + 1} key={index} data-test-id='bullet-counter'>
                        <span
                            // biome-ignore lint: sanitized content with DOMPurify
                            dangerouslySetInnerHTML={{
                                __html: sanitizeHtml(`userManual.${keyPage}.list.${key}`),
                            }}
                        />
                    </div>
                ))}
            </div>
            {noteExists && (
                <p
                    className='margin-top-5'
                    // biome-ignore lint: sanitized content with DOMPurify
                    dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(`userManual.${keyPage}.note`),
                    }}
                />
            )}
        </>
    );
});

UserManualFunctionDialogContent.displayName = 'UserManualFunctionDialogContent';

export default UserManualFunctionDialogContent;
